import { useQuery } from '@tanstack/react-query';
import type { KnowledgeGroup } from '@couch/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../api/client';
import { withCode } from './knowledge/summary';

export const KNOWLEDGE_KEY = ['knowledge'] as const;
/** The topics change only with a deploy. */
export const KNOWLEDGE_STALE_MS = 10 * 60_000;

const GROUPS: readonly { id: KnowledgeGroup; title: string }[] = [
  { id: 'start', title: 'Start here' },
  { id: 'models', title: 'Models' },
  { id: 'techniques', title: 'Techniques' },
  { id: 'comfyui', title: 'ComfyUI' },
  { id: 'media', title: 'Video, 3D and audio' },
];

/** The Knowledge tab (doc 44): the topics Claude reads before it builds a workflow, for people to look things up. */
export function KnowledgePage() {
  const [filter, setFilter] = useState('');
  const list = useQuery({ queryKey: KNOWLEDGE_KEY, queryFn: api.knowledge, staleTime: KNOWLEDGE_STALE_MS });
  const needle = filter.trim().toLowerCase();
  const topics = (list.data?.topics ?? []).filter((t) => !needle || `${t.id} ${t.title} ${t.summary}`.toLowerCase().includes(needle));
  return (
    <section aria-labelledby="knowledge-title">
      <div className="page-head">
        <h1 id="knowledge-title">Knowledge</h1>
      </div>
      <p className="muted">What Claude reads before it builds a workflow — the same topics, to look things up yourself.</p>
      <input type="search" className="knowledge-filter" aria-label="Filter topics" placeholder="Filter topics…" value={filter} onChange={(e) => { setFilter(e.target.value); }} />
      {list.isPending && <p role="status">Loading…</p>}
      {list.isError && <p role="alert" className="form-error">{errorMessage(list.error)}</p>}
      {list.isSuccess && topics.length === 0 && <p className="muted">No topic matches “{filter}”.</p>}
      {GROUPS.map((group) => {
        const inGroup = topics.filter((t) => t.group === group.id);
        if (inGroup.length === 0) return null;
        return (
          <section key={group.id} className="panel" aria-labelledby={`knowledge-${group.id}`}>
            <h2 id={`knowledge-${group.id}`}>{group.title}</h2>
            <ul className="knowledge-list">
              {inGroup.map((t) => (
                <li key={t.id}>
                  <Link to={`/knowledge/${t.id}`}>{t.title}</Link> <small className="muted">checked {t.lastVerified}</small>
                  <p className="muted">{withCode(t.summary)}</p>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </section>
  );
}
